import React, { useEffect, useRef, useState } from "react";
import Wordmark from "./Wordmark";

// Branded first-paint boot state — replaces the old bare tiny-logo LOADING
// frame, but ONLY for genuine cold boot. This is a pure presentational cover:
// it never gates or delays app initialization itself. `active` mirrors
// TasteTestApp's own S.LOADING stage, which is guaranteed true on the very
// first render (that's the component's initial state), so this is what
// paints first. The stacked wordmark plays one CSS-driven scale pulse
// (`.tt-boot-pulse`, index.css) on mount — a single ~900ms slow breath,
// never looping, settled by ~900ms with a still beat before the fade —
// while this hold timer runs independently in JS. Once `active` goes false
// (real init resolved), this holds the branded frame up for a short
// minimum so it never flashes, then fades and calls `onDone` — after which
// TasteTestApp stops rendering this component for the rest of the session,
// so any LATER S.LOADING episode (starting a new Test, opening Editions,
// etc.) falls straight through to the existing small inline spinner,
// completely unaffected.
const MIN_VISIBLE_MS = 1200;
const FADE_MS = 200;

export default function BootOverlay({ active, onDone, reducedMotion }) {
  const [fading, setFading] = useState(false);
  const mountedAtRef = useRef(Date.now());
  const firedRef = useRef(false);

  useEffect(() => {
    if (active || firedRef.current) return undefined;
    firedRef.current = true;
    const elapsed = Date.now() - mountedAtRef.current;
    const holdMs = Math.max(0, MIN_VISIBLE_MS - elapsed);
    let fadeTimer;
    const holdTimer = setTimeout(() => {
      setFading(true);
      fadeTimer = setTimeout(() => onDone && onDone(), reducedMotion ? 0 : FADE_MS);
    }, holdMs);
    return () => {
      clearTimeout(holdTimer);
      clearTimeout(fadeTimer);
    };
  }, [active, onDone, reducedMotion]);

  return (
    <div
      aria-hidden="true"
      data-testid="boot-overlay"
      className="fixed inset-0 z-[999] flex flex-col bg-canvas"
      style={{
        opacity: fading ? 0 : 1,
        transition: reducedMotion ? "opacity 1ms linear" : `opacity ${FADE_MS}ms ease-out`,
      }}
    >
      {/* Flex spacers (46/54) put the lockup's optical center at ~46% of the
          viewport — slightly above true middle — responsively, without
          relying on vh math that fights mobile dynamic-toolbar resizing. */}
      <div style={{ flex: "46 1 0%" }} />
      <div className="flex flex-col items-center px-6">
        <Wordmark
          variant="stacked"
          style={{ width: "min(80vw, 372px)" }}
          className={reducedMotion ? undefined : "tt-boot-pulse"}
        />
        <p className="mt-[30px] font-mono text-[12px] tracking-[0.32em] uppercase text-silver/70 text-center">
          Play your taste.
        </p>
      </div>
      <div style={{ flex: "54 1 0%" }} />
    </div>
  );
}
